import { dateFormats, type LabelTypeColors, type SurveyUser, useIntl } from '@capsicum/common';
import { usePartners } from '@capsicum/socios-core';
import { useCallback, useMemo } from 'react';

import { useSurveysClosed } from './useSurveysClosed';
import { useSurveysOpen } from './useSurveysOpen';
import { useSurveyStatusColor } from './useSurveyStatusColor';

export type ListSurveys = {
	closeAt: string;
	isBound: boolean;
	isSurveyClosed: boolean;
	isSurveyScheduled: boolean;
	labelProps: {
		color: LabelTypeColors;
		labelText: string;
	};
	question: string;
	status: string;
	surveyId: string;
	tokenLimit: number;
	userVote: boolean;
};

export const useSurveyList = (partnerId: string) => {
	const { getLocalizedField } = useIntl();

	const { arePartnersLoading, getPartnerBy, partnersError } = usePartners();
	const { isSurveysOpenLoading, surveysOpenData, surveysOpenError } = useSurveysOpen(partnerId);
	const { isSurveysClosedLoading, surveysClosedData, surveysClosedError } = useSurveysClosed(partnerId);
	const getSurveyStatusColor = useSurveyStatusColor();

	const partner = useMemo(() => getPartnerBy(p => p.id === partnerId), [getPartnerBy, partnerId]);

	const partnerName = partner?.name;
	const partnerImages = partner?.images;
	const partnerShortName = partner?.shortName;

	const isSurveyClosed = (surveyStatus?: string) =>
		['closed', 'closed-blockchain-pending', 'closed-release-wallet-limit'].includes(surveyStatus || 'N/A');

	const isSurveyScheduled = (surveyStatus?: string) => ['scheduled'].includes(surveyStatus || 'N/A');

	// TODO: Refactor to extract this logic to a shared hook.
	const mutateSurveyData = useCallback(
		(data?: Array<SurveyUser>): Array<ListSurveys> =>
			(data || []).map(survey => {
				const { closeAt, id, isBound, openAt, status, tokenLimit, userVote } = survey;

				const getLabelText = () => {
					if (openAt && isSurveyScheduled(status)) {
						return dateFormats.format(openAt, 'DD/MM/YYYY');
					}

					if (closeAt) {
						return dateFormats.format(closeAt, 'DD/MM/YYYY');
					}

					return '';
				};

				return {
					closeAt: closeAt ?? '',
					isBound: isBound ?? false,
					isSurveyClosed: isSurveyClosed(status),
					isSurveyScheduled: isSurveyScheduled(status),
					labelProps: {
						color: getSurveyStatusColor(status) ?? 'blue',
						labelText: getLabelText()
					},
					question: getLocalizedField('question', survey) ?? '',
					status: status ?? 'closed',
					surveyId: id ?? '',
					tokenLimit: tokenLimit ?? 0,
					userVote: userVote ?? false
				};
			}),
		[getSurveyStatusColor]
	);

	const openSurveys = useMemo(() => mutateSurveyData(surveysOpenData), [mutateSurveyData, surveysOpenData]);
	const closedSurveys = useMemo(() => mutateSurveyData(surveysClosedData), [mutateSurveyData, surveysClosedData]);

	return {
		error: partnersError || surveysOpenError || surveysClosedError,
		isLoading: arePartnersLoading || isSurveysOpenLoading || isSurveysClosedLoading,
		listSurveys: { closed: closedSurveys, opened: openSurveys },
		partnerId,
		partnerImages,
		partnerName,
		partnerShortName
	};
};
